import type { InstitutionKey } from "@design-system/InstitutionIcon/InstitutionIcon";
import { cursorMcpInstallUrl } from "./mcpEndpoint";

export enum ConnectorCategory {
    AI_TOOLS = "ai-tools",
    DEVELOPER_TOOLS = "developer-tools",
    PRODUCTIVITY_TOOLS = "productivity-tools",
}

export type CategoryFilter = "all" | ConnectorCategory;

export interface ConnectorDefinition {
    id: string;
    name: string;
    category: ConnectorCategory;
    /** The logo in the connectors catalogue and on the MCP authorize page. */
    institution: InstitutionKey;
    /** External setup guide (Kick docs) opened by the Setup guide link. */
    setupUrl: string;
    /** Direct connect link. When set, a Connect button is shown. */
    connectUrl?: string;
    matchKeywords?: string[];
    isEnabled: boolean;
}

const DOCS_BASE = "https://docs.kick.co/ai/mcp/connectors";

/** Bucket for connections that map to no enabled connector. */
export const FALLBACK_CONNECTOR_ID = "custom-mcp";

export const connectors: ConnectorDefinition[] = [
    {
        id: "claude",
        name: "Claude",
        category: ConnectorCategory.AI_TOOLS,
        institution: "claude",
        setupUrl: `${DOCS_BASE}/connect-claude`,
        matchKeywords: ["claude"],
        isEnabled: true,
    },
    {
        id: "chatgpt",
        name: "ChatGPT",
        category: ConnectorCategory.AI_TOOLS,
        institution: "chatgpt",
        setupUrl: `${DOCS_BASE}/connect-chatgpt`,
        connectUrl: `https://chatgpt.com/plugins/plugin_asdk_app_6a3c003dd068819180296a55ee4b576f`,
        matchKeywords: ["chatgpt", "chat gpt", "openai"],
        isEnabled: true,
    },
    {
        id: "codex-mcp-client",
        name: "Codex",
        category: ConnectorCategory.AI_TOOLS,
        institution: "chatgpt",
        setupUrl: `${DOCS_BASE}/connect-codex`,
        matchKeywords: ["codex"],
        isEnabled: false,
    },
    {
        id: "cursor",
        name: "Cursor",
        category: ConnectorCategory.AI_TOOLS,
        institution: "cursor",
        setupUrl: `${DOCS_BASE}/connect-cursor`,
        connectUrl: cursorMcpInstallUrl(),
        matchKeywords: ["cursor"],
        isEnabled: true,
    },
    {
        id: "gemini",
        name: "Gemini",
        category: ConnectorCategory.AI_TOOLS,
        institution: "gemini",
        setupUrl: `${DOCS_BASE}/connect-gemini`,
        matchKeywords: ["gemini"],
        isEnabled: false,
    },
    {
        id: "perplexity",
        name: "Perplexity",
        category: ConnectorCategory.AI_TOOLS,
        institution: "perplexity",
        setupUrl: `${DOCS_BASE}/connect-perplexity`,
        connectUrl: "https://www.perplexity.ai/account/connectors",
        matchKeywords: ["perplexity"],
        isEnabled: true,
    },
    {
        id: "grok",
        name: "Grok",
        category: ConnectorCategory.AI_TOOLS,
        institution: "grok",
        setupUrl: `${DOCS_BASE}/connect-grok`,
        matchKeywords: ["grok"],
        isEnabled: false,
    },
    // Must precede the generic Copilot entry: the first keyword match wins.
    {
        id: "microsoft-copilot",
        name: "Microsoft Copilot",
        category: ConnectorCategory.AI_TOOLS,
        institution: "copilot",
        setupUrl: `${DOCS_BASE}/connect-microsoft-copilot`,
        matchKeywords: ["microsoft copilot"],
        isEnabled: false,
    },
    {
        id: "copilot",
        name: "Copilot",
        category: ConnectorCategory.AI_TOOLS,
        institution: "copilot",
        setupUrl: `${DOCS_BASE}/connect-copilot`,
        matchKeywords: ["copilot"],
        isEnabled: false,
    },
    {
        id: "kick-google-sheets-addon",
        name: "Google Sheets™",
        category: ConnectorCategory.PRODUCTIVITY_TOOLS,
        institution: "google-sheets",
        setupUrl: `${DOCS_BASE}/connect-google-sheets`,
        connectUrl:
            "https://workspace.google.com/marketplace/app/kick_to_sheets/83078251972",
        matchKeywords: ["kick google sheets addon", "google sheets"],
        isEnabled: true,
    },
    {
        id: FALLBACK_CONNECTOR_ID,
        name: "Custom MCP",
        category: ConnectorCategory.DEVELOPER_TOOLS,
        institution: "mcp",
        setupUrl: `${DOCS_BASE}/connect-custom-ai-tools`,
        matchKeywords: ["custom mcp"],
        isEnabled: true,
    },
    {
        id: "custom-cli",
        name: "CLI",
        category: ConnectorCategory.DEVELOPER_TOOLS,
        institution: "cli",
        setupUrl: "https://docs.kick.co/ai/cli/cli-quickstart",
        matchKeywords: ["custom cli", "cli"],
        isEnabled: true,
    },
];

interface ConnectorVisibilityFlags {
    googleSheetsConnector: boolean;
}

export function isConnectorEnabled(
    connector: ConnectorDefinition,
    flags: ConnectorVisibilityFlags,
): boolean {
    if (!connector.isEnabled) {
        return false;
    }
    if (
        connector.id === "kick-google-sheets-addon" &&
        !flags.googleSheetsConnector
    ) {
        return false;
    }
    return true;
}

export function getAvailableCategories(
    flags: ConnectorVisibilityFlags,
): Set<ConnectorCategory> {
    return new Set(
        connectors
            .filter((connector) => isConnectorEnabled(connector, flags))
            .map((connector) => connector.category),
    );
}

function normalizeForMatch(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

/**
 * Whole-word match so that a keyword never matches inside a longer word
 * (for example "cli" must not match the client name "Kick MCP client").
 */
function matchesKeyword(normalizedName: string, keyword: string): boolean {
    const normalizedKeyword = normalizeForMatch(keyword);
    if (!normalizedKeyword) {
        return false;
    }
    return ` ${normalizedName} `.includes(` ${normalizedKeyword} `);
}

export function findConnectorByClientName(
    clientName: string | null,
): ConnectorDefinition | null {
    const normalized = normalizeForMatch(clientName ?? "");
    if (!normalized) {
        return null;
    }
    return (
        connectors.find((connector) =>
            (connector.matchKeywords ?? []).some((keyword) =>
                matchesKeyword(normalized, keyword),
            ),
        ) ?? null
    );
}

export function connectorMatchesSearch(
    connector: ConnectorDefinition,
    search: string,
): boolean {
    const normalizedSearch = normalizeForMatch(search);
    if (!normalizedSearch) {
        return true;
    }
    return [connector.name, ...(connector.matchKeywords ?? [])].some((value) =>
        normalizeForMatch(value).includes(normalizedSearch),
    );
}

export function getConnectorInstitutionForClientName(
    clientName: string | null,
): InstitutionKey {
    return findConnectorByClientName(clientName)?.institution ?? "mcp";
}
